import CfaAnalysisService from "../../../services/CfaAnalysisService";

const ANALYSIS_MODE_ENDPOINT_MAP = {
    execute: "runExecute",
    "income-analysis": "runIncomeAnalysis",
    "aml-screening": "runAmlScreening",
    "fraud-screening": "runFraudScreening",
    "contact-analysis": "runContactAnalysis",
    "profile-summary": "runProfileSummary"
};

const RESULT_ENDPOINT_MAP = {
    customerProfile: "getProfileSummary",
    regularIncome: "getRegularIncome",
    moneyFlow: "getMoneyFlow",
    amountDistribution: "getAmountDistribution",
    contactNetwork: "getContactNetwork",
    anomalyDetection: "getAnomalies",
    temporalBehavior: "getTemporalBehavior",
    transactionVelocity: "getVelocity",
    structuringDetection: "getStructuring",
    dormancyAnalysis: "getDormancy",
    layeringDetection: "getLayering",
    behavioralDeviation: "getBehavioralDeviation",
    cashTransactions: "getCashTransactions"
};

const MODE_TABS_MAP = {
    execute: null,
    "income-analysis": ["regularIncome", "moneyFlow"],
    "aml-screening": ["structuringDetection", "layeringDetection", "dormancyAnalysis", "anomalyDetection", "cashTransactions"],
    "fraud-screening": ["anomalyDetection", "transactionVelocity", "behavioralDeviation"],
    "contact-analysis": ["contactNetwork"],
    "profile-summary": null
};

const JSON_PARSE_FIELDS = [
    "profileJson", "evidenceJson", "detailJson",
    "benfordObservedJson", "histogramJson",
    "topRiskFactorsJson", "analysisCoverageJson",
    "hourlyDistributionJson", "dayOfWeekDistributionJson",
    "transactionIds",
    "topDeviationFactorsJson", "topDeviatingDimensionsJson",
    "dimensionWeightsJson",
    "targetCustomers", "parameters",
    "flowGraphJson", "largeTransactionsJson",
    "monthlyVolumeJson",
    "velocityTimeSeriesJson", "gapDistributionJson", "burstEventsJson",
    "activityTimelineJson",
    "riskTrendJson",
    "deviationTrendJson",
    "cashTransactionsJson", "cashRatioTrendJson",
    "pathNodesJson", "pathEdgeWeightsJson", "blacklistDetailsJson",
    "weightDetailsJson",
    "chainParticipantsJson", "transactionIdsJson",
    "narrativeSummaryJson", "topContactsSummaryJson",
    "regularPatternsSummaryJson", "insightsJson", "customerBriefingJson",
    "channelDistributionJson", "heatmapJson", "monthlyVolumeJson",
    "monthlyBreakdownJson", "transactionListJson",
    "salaryProgressionJson", "loanSummaryJson", "paymentHistoryJson"
];

const convertDateToISO = (ddmmyyyy) => {
    if (!ddmmyyyy) return null;
    const parts = ddmmyyyy.split(".");
    if (parts.length !== 3) return ddmmyyyy;
    return `${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`;
};

const toCamelCase = (str) => str.charAt(0).toLowerCase() + str.slice(1);

const normalizeKeys = (obj) => {
    if (Array.isArray(obj)) return obj.map(normalizeKeys);
    if (obj && typeof obj === "object" && !(obj instanceof Date)) {
        const result = {};
        Object.keys(obj).forEach((key) => {
            result[toCamelCase(key)] = normalizeKeys(obj[key]);
        });
        return result;
    }
    return obj;
};

const CFA_AUDIT = "CFA_AUDIT";

const unwrapResponse = (responseData, context) => {
    if (responseData && responseData.result !== undefined) {
        console.warn(CFA_AUDIT, `[unwrapResponse][${context}] Used .result wrapper`, { hasResult: true, resultType: typeof responseData.result, isArray: Array.isArray(responseData.result) });
        return responseData.result;
    }
    console.warn(CFA_AUDIT, `[unwrapResponse][${context}] No .result wrapper, using raw`, { dataType: typeof responseData, isNull: responseData === null });
    return responseData;
};

const parseJsonFields = (items) => {
    if (!Array.isArray(items)) return items;
    const pascalFields = JSON_PARSE_FIELDS.map((f) => f.charAt(0).toUpperCase() + f.slice(1));
    items.forEach((item) => {
        JSON_PARSE_FIELDS.forEach((field, idx) => {
            const key = typeof item[field] === "string" ? field : (typeof item[pascalFields[idx]] === "string" ? pascalFields[idx] : null);
            if (key) {
                try { item[key] = normalizeKeys(JSON.parse(item[key])); } catch (e) { console.warn(CFA_AUDIT, `[parseJsonFields] JSON parse failed for field "${key}"`, { error: e.message, valuePreview: String(item[key]).substring(0, 100) }); }
            }
        });
    });
    return items;
};

const buildPayload = (state) => {
    const params = state.analysisParameters;
    return {
        customerIds: state.customers,
        startDate: convertDateToISO(state.dateRange.startDate),
        endDate: convertDateToISO(state.dateRange.endDate),

        // Customer Profile
        lookbackMonths: params.customerProfile.lookbackMonths,

        // Regular Income
        incomePeriodicityToleranceDays: params.regularIncome.toleranceDays,
        incomeMinRecurrence: params.regularIncome.minRecurrence,
        incomeMinAmount: params.regularIncome.minAmount,
        incomePeriodHint: params.regularIncome.periodHint,

        // Money Flow
        timeGranularity: params.moneyFlow.timeGranularity,
        largeTransactionThreshold: params.moneyFlow.largeTransactionThreshold,
        minFlowCount: params.moneyFlow.minFlowCount,

        // Amount Distribution
        digitPosition: params.amountDistribution.digitPosition,
        transactionType: params.amountDistribution.transactionType,
        significanceLevel: params.amountDistribution.significanceLevel,

        // Contact Network
        minTransactionCount: params.contactNetwork.minTransactionCount,
        minTotalAmount: params.contactNetwork.minTotalAmount,
        maxHops: params.contactNetwork.maxHops,
        beta1: params.contactNetwork.beta1,
        beta2: params.contactNetwork.beta2,
        beta3: params.contactNetwork.beta3,

        // Anomaly Detection
        anomalyZScoreThreshold: params.anomalyDetection.zThreshold,
        anomalyIqrMultiplier: params.anomalyDetection.iqrMultiplier,
        anomalyMethod: params.anomalyDetection.method,
        anomalyWindowSize: params.anomalyDetection.windowSize,
        anomalyTimeGranularity: params.anomalyDetection.timeGranularity,

        // Temporal Behavior
        analysisWindow: params.temporalBehavior.analysisWindow,
        includeWeekends: params.temporalBehavior.includeWeekends,
        channelFilter: params.temporalBehavior.channelFilter,

        // Transaction Velocity
        velocityWindowHours: params.transactionVelocity.granularity === "hourly" ? 1 : 24,
        velocityBaselinePeriodWeeks: params.transactionVelocity.baselinePeriodWeeks,
        velocityMultiplier: params.transactionVelocity.velocityMultiplier,
        velocityMetric: params.transactionVelocity.metric,

        // Structuring Detection
        structuringThreshold: params.structuringDetection.reportingThreshold,
        structuringWindowDays: params.structuringDetection.windowDays,
        structuringMinFragments: params.structuringDetection.minFragments,
        structuringProximityPercent: params.structuringDetection.proximityPercent,

        // Dormancy Analysis
        minDormancyDays: params.dormancyAnalysis.dormancyThresholdDays,
        minReactivationAmount: params.dormancyAnalysis.minReactivationAmount,

        // Layering Detection
        layeringMaxHops: params.layeringDetection.maxHops,
        maxChainDays: params.layeringDetection.maxChainDays,
        amountDecayTolerance: params.layeringDetection.amountDecayTolerance,
        minChainAmount: params.layeringDetection.minChainAmount,

        // Behavioral Deviation
        baselinePeriodMonths: params.behavioralDeviation.baselinePeriodMonths,
        comparisonPeriodMonths: params.behavioralDeviation.comparisonPeriodMonths
    };
};

const actions = {
    RUN_ANALYSIS({ commit, state, dispatch }) {
        const mode = state.analysisMode;
        const serviceMethod = ANALYSIS_MODE_ENDPOINT_MAP[mode];

        const affectedTabs = MODE_TABS_MAP[mode] || Object.keys(RESULT_ENDPOINT_MAP);
        console.warn(CFA_AUDIT, `[RUN_ANALYSIS] mode=${mode}, method=${serviceMethod}, tabs=[${affectedTabs.join(", ")}], customers=${JSON.stringify(state.customers)}, dateRange=${JSON.stringify(state.dateRange)}`);

        affectedTabs.forEach((tab) => {
            commit("clearActivePoller", tab);
            commit("setLoading", { analysisType: tab, loading: true });
            commit("setBackendMessage", { analysisType: tab, message: null });
            commit("setProgress", { analysisType: tab, progress: 0 });
        });

        const payload = buildPayload(state);

        return CfaAnalysisService[serviceMethod](payload)
            .then((response) => {
                const body = response.data;
                const inner = unwrapResponse(body, "RUN_ANALYSIS");
                const analysisId = (inner && inner.analysisId) || (inner && inner.id) || null;
                const idSource = inner && inner.analysisId ? "analysisId" : (inner && inner.id ? "id" : "null");

                console.warn(CFA_AUDIT, `[RUN_ANALYSIS] Response received - analysisId=${analysisId} (source: ${idSource}), status=${inner && inner.status}, httpStatus=${response.status}`);

                if (analysisId) {
                    affectedTabs.forEach((tab) => {
                        commit("setAnalysisId", { analysisType: tab, analysisId });
                    });
                    return dispatch("POLL_AND_FETCH", { analysisId, affectedTabs });
                }

                console.warn(CFA_AUDIT, "[RUN_ANALYSIS] No analysisId - storing immediate results");
                affectedTabs.forEach((tab) => {
                    commit("setAnalysisResult", { analysisType: tab, result: { summary: inner, details: { items: [] } } });
                });
                return inner;
            })
            .catch((error) => {
                const status = error.response && error.response.status;
                console.warn(CFA_AUDIT, `[RUN_ANALYSIS] ERROR - httpStatus=${status}, message=${error.message}`, { responseData: error.response && error.response.data ? JSON.stringify(error.response.data).substring(0, 300) : null });

                if (status === 409) {
                    const msg = (error.response.data && error.response.data.errorListMessage) || "Ayni parametrelerle aktif bir analiz zaten mevcut";
                    affectedTabs.forEach((tab) => {
                        commit("setBackendMessage", { analysisType: tab, message: msg });
                    });
                } else {
                    affectedTabs.forEach((tab) => {
                        commit("clearAnalysisResult", tab);
                    });
                }
                return Promise.reject(error);
            })
            .finally(() => {
                affectedTabs.forEach((tab) => {
                    commit("setLoading", { analysisType: tab, loading: false });
                });
            });
    },

    POLL_AND_FETCH({ commit, dispatch }, { analysisId, affectedTabs }) {
        console.warn(CFA_AUDIT, `[POLL_AND_FETCH] Starting polling for analysisId=${analysisId}, tabs=[${affectedTabs.join(", ")}]`);
        const pollStartTime = Date.now();
        const poller = CfaAnalysisService.pollAnalysis(analysisId);
        poller.onProgress((percent) => {
            console.warn(CFA_AUDIT, `[POLL_AND_FETCH] Progress: ${percent}%, elapsed: ${Math.round((Date.now() - pollStartTime) / 1000)}s`);
            affectedTabs.forEach((tab) => {
                commit("setProgress", { analysisType: tab, progress: percent });
            });
        });
        affectedTabs.forEach((tab) => {
            commit("setActivePoller", { analysisType: tab, poller });
        });

        return poller
            .then((pollResponse) => {
                const elapsed = Math.round((Date.now() - pollStartTime) / 1000);
                console.warn(CFA_AUDIT, `[POLL_AND_FETCH] Polling complete in ${elapsed}s, httpStatus=${pollResponse.status}`);
                const summaryData = unwrapResponse(pollResponse.data, "POLL_AND_FETCH");

                if (summaryData && summaryData.resultSummary) {
                    let parsedSummary = summaryData.resultSummary;
                    if (typeof parsedSummary === "string") {
                        try { parsedSummary = JSON.parse(parsedSummary); } catch (e) {
                            console.warn(CFA_AUDIT, "[POLL_AND_FETCH] resultSummary JSON parse FAILED", { raw: parsedSummary.substring(0, 200) });
                        }
                    }
                    if (parsedSummary && parsedSummary.message) {
                        console.warn(CFA_AUDIT, `[POLL_AND_FETCH] Backend message: ${parsedSummary.message}`);
                        affectedTabs.forEach((tab) => {
                            commit("setBackendMessage", { analysisType: tab, message: parsedSummary.message });
                        });
                    }
                }

                console.warn(CFA_AUDIT, `[POLL_AND_FETCH] Fetching ${affectedTabs.length} tab results...`);
                const fetchPromises = affectedTabs.map((tab) =>
                    dispatch("FETCH_TAB_RESULT", { analysisId, analysisType: tab })
                );

                return Promise.all(fetchPromises).then((results) => {
                    const successCount = results.filter((r) => r !== null).length;
                    const failCount = results.filter((r) => r === null).length;
                    console.warn(CFA_AUDIT, `[POLL_AND_FETCH] All fetches done - success: ${successCount}, failed: ${failCount}`);
                    return summaryData;
                });
            })
            .catch((error) => {
                console.warn(CFA_AUDIT, `[POLL_AND_FETCH] POLL ERROR - ${error.message}`, { elapsed: `${Math.round((Date.now() - pollStartTime) / 1000)}s` });
                affectedTabs.forEach((tab) => {
                    commit("setBackendMessage", { analysisType: tab, message: error.message || null });
                    commit("clearAnalysisResult", tab);
                });
                return Promise.reject(error);
            })
            .finally(() => {
                affectedTabs.forEach((tab) => {
                    commit("clearActivePoller", tab);
                });
            });
    },

    FETCH_TAB_RESULT({ commit }, { analysisId, analysisType }) {
        const fetcherMethod = RESULT_ENDPOINT_MAP[analysisType];
        if (!fetcherMethod) return Promise.resolve(null);

        const paginationPayload = { currentPage: 1, itemsPerPage: 500, orderColumn: null, isDesc: true, filterList: [] };

        console.warn(CFA_AUDIT, `[FETCH_TAB_RESULT] Fetching ${analysisType} via ${fetcherMethod}, analysisId=${analysisId}`);

        return CfaAnalysisService[fetcherMethod](analysisId, paginationPayload)
            .then((response) => {
                const data = unwrapResponse(response.data, `FETCH_TAB_RESULT:${analysisType}`);
                const itemsSource = data && data.data ? "data.data" : (data && data.items ? "data.items" : (Array.isArray(data) ? "rawArray" : "empty"));
                const items = (data && (data.data || data.items)) || (Array.isArray(data) ? data : []);

                console.warn(CFA_AUDIT, `[FETCH_TAB_RESULT][${analysisType}] Items source: ${itemsSource}, count: ${items.length}, totalRowCount: ${data && data.totalRowCount}`);

                parseJsonFields(items);
                const normalized = normalizeKeys(items);

                if (normalized.length > 0) {
                    const sampleItem = normalized[0];
                    const sampleKeys = Object.keys(sampleItem);
                    const hasSeverity = sampleKeys.some((k) => k === "severity" || k === "severityLevel" || k === "riskLevel");
                    const hasIsAnomaly = sampleItem.isAnomaly !== undefined;
                    const hasDetectionType = sampleItem.detectionType !== undefined;
                    console.warn(CFA_AUDIT, `[FETCH_TAB_RESULT][${analysisType}] Sample item keys: [${sampleKeys.slice(0, 15).join(", ")}${sampleKeys.length > 15 ? "..." : ""}], hasSeverity: ${hasSeverity}, hasIsAnomaly: ${hasIsAnomaly}, hasDetectionType: ${hasDetectionType}`);
                }

                const result = {
                    items: normalized,
                    totalRowCount: (data && data.totalRowCount) || normalized.length,
                    totalPageCount: (data && data.totalPageCount) || 1,
                    timestamp: new Date().toISOString()
                };
                if (data && typeof data === "object" && !Array.isArray(data)) {
                    const extraKeys = [];
                    Object.keys(data).forEach((key) => {
                        const camelKey = toCamelCase(key);
                        if (result[camelKey] === undefined) {
                            const val = data[key];
                            result[camelKey] = (val && typeof val === "object") ? normalizeKeys(val) : val;
                            extraKeys.push(camelKey);
                        }
                    });
                    if (extraKeys.length > 0) {
                        console.warn(CFA_AUDIT, `[FETCH_TAB_RESULT][${analysisType}] Extra response keys copied: [${extraKeys.join(", ")}]`);
                    }
                }

                console.warn(CFA_AUDIT, `[FETCH_TAB_RESULT][${analysisType}] SUCCESS - items: ${result.items.length}, totalRowCount: ${result.totalRowCount}`);
                commit("setAnalysisResult", { analysisType, result });
                return result;
            })
            .catch((error) => {
                console.warn(CFA_AUDIT, `[FETCH_TAB_RESULT][${analysisType}] ERROR - ${error && error.message ? error.message : "Unknown error"}`, { status: error && error.status, responseData: error && error.data ? JSON.stringify(error.data).substring(0, 200) : null });
                commit("setAnalysisResult", { analysisType, result: { items: [], totalRowCount: 0, totalPageCount: 0, timestamp: new Date().toISOString() } });
                return null;
            });
    },

    CANCEL_ANALYSIS({ commit, state }) {
        const keys = Object.keys(state.activePollers);
        keys.forEach((key) => {
            commit("clearActivePoller", key);
            commit("setLoading", { analysisType: key, loading: false });
        });
    },

    FETCH_MULTI_HOP_PATHS({ commit, state }, { analysisId, pagination }) {
        commit("setMultiHopLoading", true);
        const paginationPayload = pagination || { currentPage: 1, itemsPerPage: 20, orderColumn: null, isDesc: true, filterList: [] };

        return CfaAnalysisService.getMultiHopPaths(analysisId, paginationPayload)
            .then((response) => {
                const data = unwrapResponse(response.data, "FETCH_MULTI_HOP");
                const items = (data && (data.data || data.items)) || (Array.isArray(data) ? data : []);
                parseJsonFields(items);
                const normalized = normalizeKeys(items);
                const result = {
                    items: normalized,
                    totalRowCount: (data && data.totalRowCount) || normalized.length,
                    totalPageCount: (data && data.totalPageCount) || 1
                };
                commit("setMultiHopResults", result);
                return result;
            })
            .catch(() => {
                commit("setMultiHopResults", { items: [], totalRowCount: 0, totalPageCount: 0 });
                return null;
            })
            .finally(() => {
                commit("setMultiHopLoading", false);
            });
    },

    FIND_CONNECTION({ commit }, payload) {
        commit("setConnectionLoading", true);

        return CfaAnalysisService.findConnection(payload)
            .then((response) => {
                const data = unwrapResponse(response.data, "FIND_CONNECTION");
                const paths = (data && (data.paths || data.data || data.items)) || (Array.isArray(data) ? data : []);
                parseJsonFields(paths);
                const normalized = normalizeKeys(paths);
                commit("setConnectionPaths", normalized);
                return normalized;
            })
            .catch(() => {
                commit("setConnectionPaths", []);
                return [];
            })
            .finally(() => {
                commit("setConnectionLoading", false);
            });
    },

    RETRY_ANALYSIS({ commit, dispatch, state }, { analysisType }) {
        const analysisId = state.analysisIds ? state.analysisIds[analysisType] : null;
        if (!analysisId) {
            return dispatch("RUN_ANALYSIS");
        }
        commit("setLoading", { analysisType, loading: true });
        commit("setBackendMessage", { analysisType, message: null });
        return dispatch("FETCH_TAB_RESULT", { analysisId, analysisType })
            .finally(() => {
                commit("setLoading", { analysisType, loading: false });
            });
    },

    EXPORT_REPORT(_, { format, tabs, translateFn, options }) {
        return import("../../../factory/cfa/CfaExportFactory").then((module) => {
            const CfaExportFactory = module.default;
            if (format === "pdf") {
                return CfaExportFactory.exportToPdf({ tabs, translateFn, options });
            }
            return CfaExportFactory.exportToExcel({ tabs, translateFn, options });
        });
    }
};

export default actions;
